import { Component } from '@theme/component';
import { sectionRenderer } from '@theme/section-renderer';
import { requestIdleCallback, viewTransition } from '@theme/utilities';
import { ThemeEvents } from '@theme/events';
import { PaginatedListAspectRatioHelper } from '@theme/paginated-list-aspect-ratio';

/**
 * A custom element that renders a paginated list of items.
 *
 * @typedef {object} Refs
 * @property {HTMLUListElement} [grid] - The grid element.
 * @property {HTMLSpanElement} [viewMorePrevious] - The view more previous button.
 * @property {HTMLSpanElement} [viewMoreNext] - The view more next button.
 * @property {HTMLElement[]} [cards] - The cards elements.
 *
 * @extends Component<Refs>
 */
export default class PaginatedList extends Component {
  /**
   * @type {Map<number, string>}
   */
  pages = new Map();

  /** @type {IntersectionObserver | undefined} */
  infinityScrollObserver;

  /** @type {((value: void) => void) | null} */
  #resolveNextPagePromise = null;

  /** @type {((value: void) => void) | null} */
  #resolvePreviousPagePromise = null;

  /** @type {PaginatedListAspectRatioHelper} */
  #aspectRatioHelper;

  /** @type {boolean} */
  #userHasScrolled = false;

  /** @type {'up' | 'down' | null} */
  #scrollDirection = null;

  /** @type {number} */
  #lastScrollY = 0;

  /** @type {boolean} */
  #isPreviousIntersecting = false;

  /** @type {boolean} */
  #isNextIntersecting = false;

  /** @type {boolean} */
  #isRenderingNext = false;

  /** @type {boolean} */
  #isRenderingPrevious = false;

  connectedCallback() {
    super.connectedCallback();

    if ('scrollRestoration' in history) {
      history.scrollRestoration = 'manual';
    }

    /** @type {HTMLElement | null} */
    const templateCard = this.querySelector('[ref="cardGallery"]');
    if (templateCard) {
      this.#aspectRatioHelper = new PaginatedListAspectRatioHelper({
        templateCard,
      });
    }

    this.#initScrollTracking();
    this.#fetchPage('next');
    this.#handleInitialScrollAndObserve();

    // Listen for filter updates to clear cached pages
    document.addEventListener(ThemeEvents.FilterUpdate, this.#handleFilterUpdate);
  }

  /** @type {number} */
  #touchStartY = 0;

  /** @type {number} */
  #touchStartX = 0;

  #initScrollTracking() {
    this.#lastScrollY = window.scrollY;
    this.#userHasScrolled = false;
    this.#scrollDirection = null;

    window.addEventListener('wheel', this.#handleWheel, { passive: true });
    window.addEventListener('touchstart', this.#handleTouchStart, { passive: true });
    window.addEventListener('touchmove', this.#handleTouchMove, { passive: true });
    window.addEventListener('keydown', this.#handleKeyDown, { passive: true });
    window.addEventListener('scroll', this.#handleScroll, { passive: true });
    window.addEventListener('pageshow', this.#handlePageShow);
  }

  #handlePageShow = (event) => {
    // When returning from bfcache (persisted === true), reset user scroll state
    if (event.persisted) {
      this.#userHasScrolled = false;
      this.#scrollDirection = null;
      this.#lastScrollY = window.scrollY;
    }
  };

  #handleTouchStart = (event) => {
    if (event.touches?.length) {
      this.#touchStartY = event.touches[0].clientY;
      this.#touchStartX = event.touches[0].clientX;
    }
  };

  #handleTouchMove = (event) => {
    if (!event.touches?.length) return;
    const currentY = event.touches[0].clientY;
    const currentX = event.touches[0].clientX;
    const diffY = this.#touchStartY - currentY; // diffY > 0 means finger moved up (scrolling down)
    const diffX = Math.abs(this.#touchStartX - currentX);

    // Only process genuine vertical scrolls, ignoring horizontal gallery swipes
    if (Math.abs(diffY) > diffX && Math.abs(diffY) > 6) {
      this.#userHasScrolled = true;
      this.#scrollDirection = diffY > 0 ? 'down' : 'up';
      this.#touchStartY = currentY;
      this.#touchStartX = currentX;
      this.#checkPendingIntersections();
    }
  };

  #handleWheel = (event) => {
    // Only process vertical wheel gestures, ignoring horizontal gallery swipes
    if (Math.abs(event.deltaY) > Math.abs(event.deltaX) && Math.abs(event.deltaY) > 2) {
      this.#userHasScrolled = true;
      this.#scrollDirection = event.deltaY > 0 ? 'down' : 'up';
      this.#checkPendingIntersections();
    }
  };

  #handleKeyDown = (event) => {
    if (['ArrowDown', 'PageDown', 'Space'].includes(event.code)) {
      this.#userHasScrolled = true;
      this.#scrollDirection = 'down';
      this.#checkPendingIntersections();
    } else if (['ArrowUp', 'PageUp'].includes(event.code)) {
      this.#userHasScrolled = true;
      this.#scrollDirection = 'up';
      this.#checkPendingIntersections();
    }
  };

  #handleScroll = () => {
    // Passive scroll tracking only updates last known position.
    // It intentionally NEVER sets #userHasScrolled to true because layout shifts,
    // image loading, and font rendering trigger scroll events automatically without user input.
    this.#lastScrollY = window.scrollY;
  };

  #checkPendingIntersections() {
    if (!this.#userHasScrolled) return;

    if (this.#scrollDirection === 'up' && this.#isPreviousIntersecting) {
      this.#renderPreviousPage();
    } else if (this.#scrollDirection === 'down' && this.#isNextIntersecting) {
      this.#renderNextPage();
    }
  }

  /**
   * Handles scrolling to the targeted product card before observing viewMore elements.
   * Smoothly scrolls to the targeted card, and waits for the scroll animation to settle
   * before enabling intersection observers to avoid accidental premature pagination.
   */
  #handleInitialScrollAndObserve() {
    const hash = window.location.hash?.slice(1);

    const onScrollSettle = (callback) => {
      let settled = false;
      const settle = () => {
        if (settled) return;
        settled = true;
        window.removeEventListener('scrollend', settle);
        callback();
      };

      if ('onscrollend' in window) {
        window.addEventListener('scrollend', settle, { once: true });
        // Fallback in case scroll distance was 0 or scrollend didn't fire
        setTimeout(settle, 800);
      } else {
        setTimeout(settle, 600);
      }
    };

    const scrollToHash = (callback) => {
      if (!hash) return false;
      const targetElement = document.getElementById(hash);
      if (targetElement) {
        targetElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
        onScrollSettle(callback);
        return true;
      }
      return false;
    };

    const enableObserving = () => {
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          this.#lastScrollY = window.scrollY;
          this.#observeViewMore();
        });
      });
    };

    if (hash) {
      const scrolled = scrollToHash(enableObserving);
      if (!scrolled) {
        window.addEventListener(
          'DOMContentLoaded',
          () => {
            if (!scrollToHash(enableObserving)) {
              enableObserving();
            }
          },
          { once: true }
        );
        return;
      }
      return;
    }

    enableObserving();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    if (this.infinityScrollObserver) {
      this.infinityScrollObserver.disconnect();
    }
    window.removeEventListener('wheel', this.#handleWheel);
    window.removeEventListener('touchstart', this.#handleTouchStart);
    window.removeEventListener('touchmove', this.#handleTouchMove);
    window.removeEventListener('keydown', this.#handleKeyDown);
    window.removeEventListener('scroll', this.#handleScroll);
    window.removeEventListener('pageshow', this.#handlePageShow);
    // Remove the filter update listener
    document.removeEventListener(ThemeEvents.FilterUpdate, this.#handleFilterUpdate);
  }

  #observeViewMore() {
    const { viewMorePrevious, viewMoreNext } = this.refs;

    // Return if neither element exists
    if (!viewMorePrevious && !viewMoreNext) return;

    // Create observer if it doesn't exist
    if (!this.infinityScrollObserver) {
      this.infinityScrollObserver = new IntersectionObserver(
        async (entries) => {
          // Wait for any in-progress view transitions to finish
          if (viewTransition.current) await viewTransition.current;

          for (const entry of entries) {
            const { viewMorePrevious, viewMoreNext } = this.refs;

            if (entry.target === viewMorePrevious) {
              this.#isPreviousIntersecting = entry.isIntersecting;
              if (this.#userHasScrolled && entry.isIntersecting && this.#scrollDirection === 'up') {
                this.#renderPreviousPage();
              }
            } else if (entry.target === viewMoreNext) {
              this.#isNextIntersecting = entry.isIntersecting;
              if (this.#userHasScrolled && entry.isIntersecting && this.#scrollDirection === 'down') {
                this.#renderNextPage();
              }
            }
          }
        },
        {
          rootMargin: '100px',
        }
      );
    }

    // Observe the view more elements
    if (viewMorePrevious) {
      this.infinityScrollObserver.observe(viewMorePrevious);
    }

    if (viewMoreNext) {
      this.infinityScrollObserver.observe(viewMoreNext);
    }
  }

  /**
   * @param {{ page: number, url?: URL } | undefined} pageInfo - The page info
   * @returns {boolean} Whether to use the page
   */
  #shouldUsePage(pageInfo) {
    if (!pageInfo) return false;

    const { grid } = this.refs;
    const lastPage = grid?.dataset.lastPage;

    if (!lastPage || pageInfo.page < 1 || pageInfo.page > Number(lastPage)) return false;

    return true;
  }

  /**
   * @param {"previous" | "next"} type
   */
  async #fetchPage(type) {
    const page = this.#getPage(type);

    // Always resolve the promise, even if we can't fetch the page
    const resolvePromise = () => {
      if (type === 'next') {
        this.#resolveNextPagePromise?.();
        this.#resolveNextPagePromise = null;
      } else {
        this.#resolvePreviousPagePromise?.();
        this.#resolvePreviousPagePromise = null;
      }
    };

    if (!page || !this.#shouldUsePage(page)) {
      // Resolve the promise even if we can't fetch
      resolvePromise();
      return;
    }

    await this.#fetchSpecificPage(page.page, page.url);
    resolvePromise();
  }

  /**
   * @param {number} pageNumber - The page number to fetch
   * @param {URL} [url] - Optional URL, will be constructed if not provided
   */
  async #fetchSpecificPage(pageNumber, url = undefined) {
    const pageInfo = { page: pageNumber, url };

    if (!url) {
      const newUrl = new URL(window.location.href);
      newUrl.searchParams.set('page', pageNumber.toString());
      newUrl.hash = '';
      pageInfo.url = newUrl;
    }

    if (!this.#shouldUsePage(pageInfo)) return;
    const pageContent = await sectionRenderer.getSectionHTML(this.sectionId, true, pageInfo.url);
    this.pages.set(pageNumber, pageContent);
  }

  async #renderNextPage() {
    if (this.#isRenderingNext) return;
    this.#isRenderingNext = true;

    try {
      const { grid } = this.refs;

      if (!grid) return;

      const nextPage = this.#getPage('next');

      if (!nextPage || !this.#shouldUsePage(nextPage)) return;
      let nextPageItemElements = this.#getGridForPage(nextPage.page);

      if (!nextPageItemElements) {
        const promise = new Promise((res) => {
          this.#resolveNextPagePromise = res;
        });

        // Trigger the fetch for this page
        this.#fetchPage('next');

        await promise;
        nextPageItemElements = this.#getGridForPage(nextPage.page);
        if (!nextPageItemElements) return;
      }

      grid.append(...nextPageItemElements);

      this.#aspectRatioHelper?.processNewElements();

      history.replaceState('', '', nextPage.url.toString());

      requestIdleCallback(() => {
        this.#fetchPage('next');
      });
    } finally {
      this.#isRenderingNext = false;
    }
  }

  async #renderPreviousPage() {
    if (this.#isRenderingPrevious) return;
    this.#isRenderingPrevious = true;

    try {
      const { grid } = this.refs;

      if (!grid) return;

      const previousPage = this.#getPage('previous');
      if (!previousPage || !this.#shouldUsePage(previousPage)) return;

      let previousPageItemElements = this.#getGridForPage(previousPage.page);
      if (!previousPageItemElements) {
        const promise = new Promise((res) => {
          this.#resolvePreviousPagePromise = res;
        });

        // Trigger the fetch for this page
        this.#fetchPage('previous');

        await promise;
        previousPageItemElements = this.#getGridForPage(previousPage.page);
        if (!previousPageItemElements) return;
      }

      // Find an element currently in the viewport to act as scroll anchor
      const anchorElement =
        Array.from(grid.children).find((el) => {
          const rect = el.getBoundingClientRect();
          return rect.bottom > 0 && rect.top < window.innerHeight;
        }) || grid.firstElementChild;

      const oldTop = anchorElement ? anchorElement.getBoundingClientRect().top : 0;

      // Prepend the new elements
      grid.prepend(...previousPageItemElements);

      this.#aspectRatioHelper?.processNewElements();

      history.replaceState('', '', previousPage.url.toString());

      // If native browser scroll anchoring did not compensate, adjust scroll position
      if (anchorElement) {
        const newTop = anchorElement.getBoundingClientRect().top;
        const delta = newTop - oldTop;
        if (Math.abs(delta) > 1) {
          window.scrollBy({
            top: delta,
            behavior: 'instant',
          });
        }
      }

      requestIdleCallback(() => {
        this.#fetchPage('previous');
      });
    } finally {
      this.#isRenderingPrevious = false;
    }
  }

  /**
   * @param {"previous" | "next"} type
   * @returns {{ page: number, url: URL } | undefined}
   */
  #getPage(type) {
    const cards = Array.from(this.querySelectorAll(':scope > [ref="grid"] > [ref="cards[]"], :scope [ref="cards[]"]'));
    const isPrevious = type === 'previous';

    if (!cards.length) return;

    const targetCard = cards[isPrevious ? 0 : cards.length - 1];

    if (!targetCard) return;

    const currentCardPage = Number(targetCard.dataset.page);
    const page = isPrevious ? currentCardPage - 1 : currentCardPage + 1;

    const url = new URL(window.location.href);
    url.searchParams.set('page', page.toString());
    url.hash = '';

    return {
      page,
      url,
    };
  }

  /**
   * @param {number} page
   * @returns {NodeListOf<Element> | undefined}
   */
  #getGridForPage(page) {
    const pageHTML = this.pages.get(page);

    if (!pageHTML) return;

    const parsedPage = new DOMParser().parseFromString(pageHTML, 'text/html');
    const gridElement = parsedPage.querySelector('[ref="grid"]');
    if (!gridElement) return;
    return gridElement.querySelectorAll(':scope > [ref="cards[]"]');
  }

  get sectionId() {
    const id = this.getAttribute('section-id');

    if (!id) throw new Error('The section-id attribute is required');

    return id;
  }

  /**
   * Handle filter updates by clearing cached pages
   */
  #handleFilterUpdate = () => {
    this.pages.clear();

    // Resolve any pending promises to unblock waiting renders
    this.#resolveNextPagePromise?.();
    this.#resolvePreviousPagePromise?.();

    this.#resolveNextPagePromise = null;
    this.#resolvePreviousPagePromise = null;

    // Store the current lastPage value to detect when it changes
    const currentLastPage = this.refs.grid?.dataset.lastPage;

    // We need to wait for the DOM to be updated with the new filtered content
    // Using mutation observer to detect when the grid actually updates
    const observer = new MutationObserver(() => {
      // Check if data-last-page changed
      const newLastPage = this.refs.grid?.dataset.lastPage;

      if (newLastPage !== currentLastPage) {
        observer.disconnect();

        // Check if component is still connected
        if (!this.isConnected) {
          return;
        }

        // Now the DOM has been updated with the new filtered content
        this.#observeViewMore();

        // Fetch the next page
        this.#fetchPage('next');
      }
    });

    // Observe the grid for changes
    const { grid } = this.refs;
    if (grid) {
      observer.observe(grid, {
        attributes: true,
        attributeFilter: ['data-last-page'],
        childList: true, // Also watch for child changes in case the whole grid is replaced
      });

      // Set a timeout as a fallback in case the mutation never fires
      setTimeout(() => {
        if (observer) {
          observer.disconnect();
        }
      }, 3000);
    }
  };
}
